// frontend/src/widgets/PricebooksAdminWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-02-16
// Updated:      2026-09-09 13:17 BST
//
// Description:
//   Pricebooks admin widget (tenant-scoped; never global).
//   - Lists/searches pricebooks via GET /api/quote-catalog/pricebooks
//   - Creates via POST /api/quote-catalog/pricebooks
//   - Updates via PUT /api/quote-catalog/pricebooks/{id}
//   - Soft-deletes via DELETE /api/quote-catalog/pricebooks/{id} (deactivate)
//
// Scope rules (DB-enforced):
//   - XOR scope: customer_id OR resource_id OR neither (tenant-wide).
//   - Single default per tenant enforced by DB trigger/index.
//
// IMPORTANT:
//   Uses shared Axios instance ONLY.
//   Does NOT assume customers/resources endpoints exist; attempts to load them if available,
//   but always allows direct numeric IDs to avoid blocking progress.
//
// NOTE (2026-02-16):
//   Manual add UX for pricebook items mirrors QuoteCreateModal "Manual item" logic:
//   - service (FREE TEXT) + unit price + VAT% selector
//   - VAT% can auto-map to a tax_rate_id if a matching tax rate exists in /tax-rates
//   - commits add via POST and refreshes list; no regressions to existing edit-row behaviour
//
// Mobile Responsiveness (2026-09-09):
//   - Preserves all pricebook/header/item CRUD, validation, scope and VAT/tax logic.
//   - Main widget uses natural mobile height; desktop retains its 420px minimum.
//   - Header, search controls and pricebook action rows stack on narrow screens.
//   - Pricebook metadata changes from four columns to two/mobile-one as space narrows.
//   - Create/Edit modal becomes near-full-screen on mobile with its own vertical scroll.
//   - Code/Label, Flags/Scope, scope selectors and all item-editor grids collapse
//     to single-column mobile layouts while retaining desktop columns.
//   - Manual item controls stack on mobile; Add becomes a full-width touch target.
//   - Existing PricebookItemRow blur/Enter commit behaviour is preserved exactly.
//   - Existing react-grid-layout event-capture protection remains unchanged.
//
// Operational Notes:
//   - No API routes, request payloads, fallback lookups or tenant-scope behaviour changed.
//   - No duplicate mobile render tree is introduced.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";

// ---- helpers ---------------------------------------------------------------
const DEBUG = false;

const norm = (v) => String(v ?? "").trim();
const stripTrailingSlashes = (v) => norm(v).replace(/\/+$/, "");
const safeId = (v) => {
  const s = stripTrailingSlashes(v);
  return s ? s : "";
};

const toIntOrNull = (v) => {
  const s = norm(v);
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 1 ? Math.trunc(n) : null;
};

const toNumOrNull = (v) => {
  const s = norm(v);
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const axiosErr = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

const scopeLabel = (pb) => {
  const cid = pb?.customer_id ?? null;
  const rid = pb?.resource_id ?? null;
  if (cid && rid) return "INVALID (customer+resource)"; // should never happen (DB check)
  if (cid) return `Customer #${cid}`;
  if (rid) return `Resource #${rid}`;
  return "Tenant-wide";
};

// Resolve service text from whatever shape backend returns.
const getServiceText = (it) => {
  const candidates = [
    it?.description,
    it?.service_name,
    it?.service_text,
    it?.name,
    it?.title,
    it?.service?.name,
    it?.service?.label,
    it?.service?.title,
  ];
  for (const c of candidates) {
    const s = norm(c);
    if (s) return s;
  }
  return "";
};

// VAT% -> fraction (0.20) helper (same semantics as QuoteCreateModal)
const nnum = (v, fallback = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
};

const pctToRate = (pct) => {
  const p = nnum(pct, 0);
  if (p <= 0) return 0;
  if (p > 0 && p <= 1) return p;
  return p / 100.0;
};

const rateToPct = (rate) => {
  const r = nnum(rate, 0);
  if (r <= 0) return 0;
  if (r > 1.000001) return r;
  return r * 100.0;
};

// Find a tax rate row by percent (0/5/20) best-effort.
// taxRates rows are expected to have {id, rate} where rate is fraction (0.20).
const findTaxRateIdForPct = (taxRates, pct) => {
  const target = pctToRate(pct);
  const arr = Array.isArray(taxRates) ? taxRates : [];
  const eps = 0.0000001;
  const hit = arr.find((t) => {
    const r = Number(t?.rate);
    if (!Number.isFinite(r)) return false;
    return Math.abs(r - target) <= eps;
  });
  const id = hit?.id;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
};

// react-grid-layout captures pointerdown/mousedown.
// DO NOT preventDefault on form controls (it breaks focus/select/date pickers).
// Only stop propagation to avoid grid drag hijacking.
const stopGridEvent = (e) => {
  try {
    e.stopPropagation?.();
  } catch {}
};

const stopGridCaptureProps = {
  onPointerDownCapture: stopGridEvent,
  onMouseDownCapture: stopGridEvent,
  onTouchStartCapture: stopGridEvent,
};

// ----------------------------------------------------------------------------

export default function PricebooksAdminWidget() {
  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [items, setItems] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [q, setQ] = useState("");
  const qRef = useRef("");

  const [customers, setCustomers] = useState([]); // best-effort
  const [resources, setResources] = useState([]); // best-effort

  // Catalog lookups (best-effort)
  const [services, setServices] = useState([]); // retained, not required
  const [taxRates, setTaxRates] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create|edit
  const [editing, setEditing] = useState(null);

  // form state (pricebook header)
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [active, setActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);

  // scope mode: tenant|customer|resource
  const [scopeMode, setScopeMode] = useState("tenant");
  const [customerId, setCustomerId] = useState("");
  const [resourceId, setResourceId] = useState("");

  // Items state
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsErr, setItemsErr] = useState("");
  const [pbItems, setPbItems] = useState([]);
  const [itemsIncludeInactive, setItemsIncludeInactive] = useState(false);

  // New item form (manual UX mirroring QuoteCreateModal)
  // IMPORTANT: Service is FREE-TEXT here (no picker).
  const [newServiceText, setNewServiceText] = useState("");
  const [newUnitPrice, setNewUnitPrice] = useState("");
  const [newUnitCost, setNewUnitCost] = useState("");
  const [newTaxRateId, setNewTaxRateId] = useState(""); // optional override; can be auto-derived from VAT%
  const [newVatPct, setNewVatPct] = useState("20");
  const [newEffFrom, setNewEffFrom] = useState("");
  const [newEffTo, setNewEffTo] = useState("");
  const [newItemActive, setNewItemActive] = useState(true);
  const [newItemBusy, setNewItemBusy] = useState(false);

  const card = useMemo(
    () => ({
      background: "#fff",
      borderRadius: 12,
      padding: 14,
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      minHeight: 0,
      boxSizing: "border-box",
      overflowWrap: "anywhere",
    }),
    []
  );

  const titleStyle = useMemo(
    () => ({
      fontWeight: 900,
      marginBottom: 10,
      fontFamily: theme?.font_secondary || theme?.font_primary || "inherit",
      display: "flex",
      gap: 10,
      minWidth: 0,
    }),
    [theme]
  );

  const input = useMemo(
    () => ({
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      padding: "10px 10px",
      borderRadius: 10,
      border: "1px solid #e5e7eb",
      outline: "none",
      fontFamily: theme?.font_primary || "inherit",
      fontWeight: 800,
    }),
    [theme]
  );

  const select = useMemo(
    () => ({
      ...input,
      background: "#fff",
      cursor: "pointer",
    }),
    [input]
  );

  const buttonAction = useMemo(
    () => ({
      border: 0,
      borderRadius: 10,
      padding: "10px 12px",
      fontWeight: 900,
      cursor: "pointer",
      fontFamily: theme?.font_primary || "inherit",
      background: theme?.button_action?.normal || theme?.primary_color || "#111827",
      color: "#fff",
    }),
    [theme]
  );

  const buttonGeneral = useMemo(
    () => ({
      ...buttonAction,
      background: theme?.button_general?.normal || "#111827",
    }),
    [buttonAction, theme]
  );

  const buttonDisabled = useMemo(
    () => ({
      ...buttonAction,
      background: "#e5e7eb",
      color: "#6b7280",
      cursor: "not-allowed",
    }),
    [buttonAction]
  );

  const badge = useMemo(
    () => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 999,
      border: "1px solid #e5e7eb",
      fontWeight: 900,
      fontSize: 12,
      color: "#111827",
      background: "#fff",
      fontFamily: theme?.font_primary || "inherit",
    }),
    [theme]
  );

  const urls = useMemo(
    () => ({
      list: "/api/quote-catalog/pricebooks",
      create: "/api/quote-catalog/pricebooks",
      services: "/api/quote-catalog/services",
      taxRates: "/api/quote-catalog/tax-rates",
    }),
    []
  );

  const resetItemForm = () => {
    setNewServiceText("");
    setNewUnitPrice("");
    setNewUnitCost("");
    setNewTaxRateId("");
    setNewVatPct("20");
    setNewEffFrom("");
    setNewEffTo("");
    setNewItemActive(true);
    setNewItemBusy(false);
  };

  const resetForm = () => {
    setCode("");
    setLabel("");
    setActive(true);
    setIsDefault(false);
    setScopeMode("tenant");
    setCustomerId("");
    setResourceId("");
    setEditing(null);
    setItemsErr("");
    setPbItems([]);
    setItemsIncludeInactive(false);
    resetItemForm();
  };

  const openCreate = () => {
    setMode("create");
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (pb) => {
    setMode("edit");
    setEditing(pb || null);

    setCode(norm(pb?.code));
    setLabel(norm(pb?.label));
    setActive(Boolean(pb?.active));
    setIsDefault(Boolean(pb?.is_default));

    const cid = pb?.customer_id ?? null;
    const rid = pb?.resource_id ?? null;

    if (cid && rid) {
      setScopeMode("tenant");
      setCustomerId(String(cid));
      setResourceId(String(rid));
    } else if (cid) {
      setScopeMode("customer");
      setCustomerId(String(cid));
      setResourceId("");
    } else if (rid) {
      setScopeMode("resource");
      setResourceId(String(rid));
      setCustomerId("");
    } else {
      setScopeMode("tenant");
      setCustomerId("");
      setResourceId("");
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setErr("");
    setItemsErr("");
  };

  const loadLookupsBestEffort = async () => {
    const tries = [
      { name: "customers", url: "/api/customers" },
      { name: "customers", url: "/api/crm/customers" },
      { name: "resources", url: "/api/resources" },
      { name: "resources", url: "/api/crm/resources" },
    ];

    const nextCustomers = [];
    const nextResources = [];

    await Promise.all(
      tries.map(async (t) => {
        try {
          const res = await api.get(t.url);
          const arr = Array.isArray(res.data) ? res.data : [];
          if (t.name === "customers" && arr.length) nextCustomers.push(...arr);
          if (t.name === "resources" && arr.length) nextResources.push(...arr);
        } catch {
          // ignore
        }
      })
    );

    const dedupeById = (arr) => {
      const m = new Map();
      for (const x of arr) {
        const id = x?.id;
        if (id == null) continue;
        if (!m.has(id)) m.set(id, x);
      }
      return Array.from(m.values());
    };

    const dc = dedupeById(nextCustomers);
    const dr = dedupeById(nextResources);

    if (dc.length) setCustomers(dc);
    if (dr.length) setResources(dr);

    try {
      const sRes = await api.get(urls.services, { params: { include_inactive: false } });
      setServices(Array.isArray(sRes.data) ? sRes.data : []);
    } catch {
      setServices([]);
    }

    try {
      const tRes = await api.get(urls.taxRates, { params: { include_inactive: false } });
      setTaxRates(Array.isArray(tRes.data) ? tRes.data : []);
    } catch {
      setTaxRates([]);
    }
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    qRef.current = q;

    try {
      const res = await api.get(urls.list, { params: { include_inactive: includeInactive } });
      const arr = Array.isArray(res.data) ? res.data : [];
      setItems(arr);
    } catch (e) {
      setErr(axiosErr(e, "Failed to load pricebooks"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPricebookItems = async (pricebookId) => {
    const pbid = safeId(pricebookId);
    if (!pbid) {
      setPbItems([]);
      return;
    }

    setItemsLoading(true);
    setItemsErr("");

    try {
      const res = await api.get(`/api/quote-catalog/pricebooks/${pbid}/items`, {
        params: { include_inactive: itemsIncludeInactive },
      });
      setPbItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setItemsErr(axiosErr(e, "Failed to load pricebook items"));
      setPbItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadLookupsBestEffort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  useEffect(() => {
    if (!modalOpen) return;
    if (mode !== "edit") return;
    const pbid = editing?.id;
    if (!pbid) return;
    loadPricebookItems(pbid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, mode, editing?.id, itemsIncludeInactive]);

  useEffect(() => {
    if (!modalOpen) return;
    if (mode !== "edit") return;
    if (norm(newTaxRateId)) return;
    const mapped = findTaxRateIdForPct(taxRates, newVatPct);
    if (mapped) setNewTaxRateId(String(mapped));
  }, [modalOpen, mode, newVatPct, newTaxRateId, taxRates]);

  const filtered = useMemo(() => {
    const needle = norm(q).toLowerCase();
    if (!needle) return items;

    return items.filter((pb) => {
      const hay = `${pb?.code ?? ""} ${pb?.label ?? ""} ${scopeLabel(pb)} ${pb?.id ?? ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [items, q]);

  const validatePayload = () => {
    const c = norm(code);
    const l = norm(label);

    if (mode === "create" && !c) return "code is required";
    if (!l) return "label is required";

    const cid = toIntOrNull(customerId);
    const rid = toIntOrNull(resourceId);

    if (scopeMode === "customer" && !cid) return "customer_id is required for customer-scoped pricebooks";
    if (scopeMode === "resource" && !rid) return "resource_id is required for resource-scoped pricebooks";

    if (scopeMode === "tenant" && (cid || rid)) return "scope mismatch: tenant-wide must not set customer_id/resource_id";
    if (scopeMode === "customer" && rid) return "scope invalid: choose customer OR resource (not both)";
    if (scopeMode === "resource" && cid) return "scope invalid: choose resource OR customer (not both)";

    return "";
  };

  const buildPayload = () => {
    const cid = scopeMode === "customer" ? toIntOrNull(customerId) : null;
    const rid = scopeMode === "resource" ? toIntOrNull(resourceId) : null;

    const base = {
      label: norm(label),
      is_default: Boolean(isDefault),
      active: Boolean(active),
      customer_id: cid,
      resource_id: rid,
    };

    if (mode === "create") return { code: norm(code), ...base };
    return base;
  };

  const save = async () => {
    const v = validatePayload();
    if (v) {
      setErr(v);
      return;
    }

    setErr("");
    const payload = buildPayload();

    try {
      if (mode === "create") {
        const res = await api.post(urls.create, payload);
        const created = res?.data || null;

        if (created?.id) {
          setMode("edit");
          setEditing(created);
          setCode(norm(created?.code));
          setLabel(norm(created?.label));
          setActive(Boolean(created?.active));
          setIsDefault(Boolean(created?.is_default));

          const cid = created?.customer_id ?? null;
          const rid = created?.resource_id ?? null;
          if (cid) {
            setScopeMode("customer");
            setCustomerId(String(cid));
            setResourceId("");
          } else if (rid) {
            setScopeMode("resource");
            setResourceId(String(rid));
            setCustomerId("");
          } else {
            setScopeMode("tenant");
            setCustomerId("");
            setResourceId("");
          }

          resetItemForm();
          await load();
          await loadPricebookItems(created.id);
          return;
        }

        closeModal();
        await load();
      } else {
        const id = safeId(editing?.id);
        if (!id) {
          setErr("Invalid pricebook id");
          return;
        }
        await api.put(`/api/quote-catalog/pricebooks/${id}`, payload);
        await load();
        closeModal();
      }
    } catch (e) {
      setErr(axiosErr(e, "Failed to save pricebook"));
    }
  };

  const deactivate = async (pb) => {
    const id = safeId(pb?.id);
    if (!id) {
      setErr("Invalid pricebook id");
      return;
    }

    setErr("");

    const ok = window.confirm(
      `Deactivate pricebook "${pb?.label ?? pb?.code ?? pb?.id}"?\n\nThis is a soft delete (active=false). Quotes can keep historical references.`
    );
    if (!ok) return;

    try {
      await api.delete(`/api/quote-catalog/pricebooks/${id}`);
      await load();
      if (editing?.id === pb?.id) closeModal();
    } catch (e) {
      setErr(axiosErr(e, "Failed to deactivate pricebook"));
    }
  };

  // ---- Items (free-text service) -------------------------------------------

  const validateNewItem = () => {
    const desc = norm(newServiceText);
    const up = toNumOrNull(newUnitPrice);

    if (!desc) return "service is required";
    if (up == null || up < 0) return "unit_price is required (>= 0)";

    const uc = toNumOrNull(newUnitCost);
    if (uc != null && uc < 0) return "unit_cost must be >= 0";

    const tr = toIntOrNull(newTaxRateId);
    if (newTaxRateId && !tr) return "tax_rate_id must be numeric";

    return "";
  };

  const addItem = async () => {
    const pbid = safeId(editing?.id);
    if (!pbid) {
      setItemsErr("No pricebook selected");
      return;
    }

    const v = validateNewItem();
    if (v) {
      setItemsErr(v);
      return;
    }

    setItemsErr("");
    setNewItemBusy(true);

    const explicitTaxId = norm(newTaxRateId) ? toIntOrNull(newTaxRateId) : null;
    const mappedTaxId = explicitTaxId ?? findTaxRateIdForPct(taxRates, newVatPct);

    const serviceText = norm(newServiceText);

    // Send multiple aliases to match backend reality (prevents “saved but blank”).
    const payload = {
      description: serviceText,
      service_name: serviceText,
      service_text: serviceText,

      unit_price: Number(newUnitPrice),
      unit_cost: norm(newUnitCost) ? Number(newUnitCost) : null,
      tax_rate_id: mappedTaxId,
      active: Boolean(newItemActive),
      effective_from: newEffFrom ? String(newEffFrom) : null,
      effective_to: newEffTo ? String(newEffTo) : null,
    };

    try {
      await api.post(`/api/quote-catalog/pricebooks/${pbid}/items`, payload);
      resetItemForm();
      await loadPricebookItems(pbid);
    } catch (e) {
      setItemsErr(axiosErr(e, "Failed to add item"));
    } finally {
      setNewItemBusy(false);
    }
  };

  const patchItem = async (itemId, updates) => {
    const pbid = safeId(editing?.id);
    const iid = safeId(itemId);
    if (!pbid || !iid) return;

    setItemsErr("");

    try {
      await api.put(`/api/quote-catalog/pricebooks/${pbid}/items/${iid}`, updates);
      await loadPricebookItems(pbid);
    } catch (e) {
      setItemsErr(axiosErr(e, "Failed to update item"));
    }
  };

  const deactivateItem = async (itemId) => {
    const pbid = safeId(editing?.id);
    const iid = safeId(itemId);
    if (!pbid || !iid) return;

    setItemsErr("");

    const ok = window.confirm("Deactivate this item? (soft delete: active=false)");
    if (!ok) return;

    try {
      await api.delete(`/api/quote-catalog/pricebooks/${pbid}/items/${iid}`);
      await loadPricebookItems(pbid);
    } catch (e) {
      setItemsErr(axiosErr(e, "Failed to deactivate item"));
    }
  };

  // --------------------------------------------------------------------------

  const renderScopePicker = () => {
    const cidInt = toIntOrNull(customerId);
    const ridInt = toIntOrNull(resourceId);

    const customerName = (() => {
      if (!cidInt) return "";
      const c = customers.find((x) => Number(x?.id) === Number(cidInt));
      return norm(c?.name || c?.label || c?.company_name || c?.title);
    })();

    const resourceName = (() => {
      if (!ridInt) return "";
      const r = resources.find((x) => Number(x?.id) === Number(ridInt));
      return norm(r?.name || r?.display_name || `${norm(r?.first_name)} ${norm(r?.last_name)}`.trim());
    })();

    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-[180px_1fr] md:items-center">
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Scope</div>
          <select
            style={select}
            value={scopeMode}
            onChange={(e) => {
              const next = e.target.value;

              // CRITICAL FIX:
              // When switching scope modes, clear the irrelevant id so the XOR validation
              // does not block legitimate scope changes (customer -> resource, etc).
              if (next === "tenant") {
                setCustomerId("");
                setResourceId("");
              } else if (next === "customer") {
                setResourceId("");
              } else if (next === "resource") {
                setCustomerId("");
              }

              setScopeMode(next);
            }}
            {...stopGridCaptureProps}
          >
            <option value="tenant">Tenant-wide</option>
            <option value="customer">Per customer</option>
            <option value="resource">Per resource</option>
          </select>
        </div>

        {scopeMode === "customer" ? (
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-[180px_1fr] md:items-center">
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Customer</div>
            {customers.length ? (
              <select style={select} value={customerId} onChange={(e) => setCustomerId(e.target.value)} {...stopGridCaptureProps}>
                <option value="">Select customer…</option>
                {customers
                  .slice()
                  .sort((a, b) => Number(a?.id) - Number(b?.id))
                  .map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      #{c.id} — {c?.name || c?.label || c?.company_name || c?.title || "Customer"}
                    </option>
                  ))}
              </select>
            ) : (
              <input
                style={input}
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                placeholder="customer_id (numeric)"
                {...stopGridCaptureProps}
              />
            )}
            {customerName ? (
              <div className="text-xs font-black text-gray-500 md:col-start-2 md:col-end-3">Selected: {customerName}</div>
            ) : null}
          </div>
        ) : null}

        {scopeMode === "resource" ? (
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-[180px_1fr] md:items-center">
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Resource</div>
            {resources.length ? (
              <select style={select} value={resourceId} onChange={(e) => setResourceId(e.target.value)} {...stopGridCaptureProps}>
                <option value="">Select resource…</option>
                {resources
                  .slice()
                  .sort((a, b) => Number(a?.id) - Number(b?.id))
                  .map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      #{r.id} — {r?.name || r?.display_name || `${r?.first_name ?? ""} ${r?.last_name ?? ""}`.trim() || "Resource"}
                    </option>
                  ))}
              </select>
            ) : (
              <input
                style={input}
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                placeholder="resource_id (numeric)"
                {...stopGridCaptureProps}
              />
            )}
            {resourceName ? (
              <div className="text-xs font-black text-gray-500 md:col-start-2 md:col-end-3">Selected: {resourceName}</div>
            ) : null}
          </div>
        ) : null}

        <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>
          DB enforces XOR scope. Default is tenant-level only (single default per tenant).
        </div>
      </div>
    );
  };

  const renderItemsPanel = () => {
    if (mode !== "edit" || !editing?.id) {
      return (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
          <div style={{ fontWeight: 900, marginBottom: 6 }}>Items</div>
          <div style={{ color: "#6b7280", fontWeight: 900, fontSize: 12 }}>
            Create the pricebook first. After creation, this modal switches to Edit mode and you can add items.
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #e5e7eb", display: "grid", gap: 10 }}>
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
          <div style={{ fontWeight: 900 }}>Items</div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 900, color: "#111827" }}>
            <input
              type="checkbox"
              checked={itemsIncludeInactive}
              onChange={(e) => setItemsIncludeInactive(e.target.checked)}
              {...stopGridCaptureProps}
            />
            Include inactive
          </label>
        </div>

        {itemsErr ? <div style={{ color: "#b91c1c", fontWeight: 900 }}>{itemsErr}</div> : null}
        {itemsLoading ? <div style={{ fontWeight: 900 }}>Loading items…</div> : null}

        <div style={{ display: "grid", gap: 10, border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <div
            style={{
              padding: "10px 12px",
              borderBottom: "1px solid #e5e7eb",
              background: "#f9fafb",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontWeight: 900, fontSize: 12 }}>Manual item</div>
            <div style={{ fontWeight: 900, fontSize: 12, color: "#6b7280" }}>{newItemBusy ? "Working…" : "Ready"}</div>
          </div>

          <div className="grid grid-cols-1 gap-2.5 p-3 md:grid-cols-[minmax(0,1fr)_140px_110px_auto] md:items-end">
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Service</div>
              <input
                style={input}
                value={newServiceText}
                onChange={(e) => setNewServiceText(e.target.value)}
                placeholder="Service (free text)"
                disabled={newItemBusy}
                {...stopGridCaptureProps}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Unit price</div>
              <input
                style={input}
                value={newUnitPrice}
                onChange={(e) => setNewUnitPrice(e.target.value)}
                placeholder="0.00"
                type="number"
                disabled={newItemBusy}
                {...stopGridCaptureProps}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>VAT %</div>
              <select
                style={select}
                value={newVatPct}
                onChange={(e) => {
                  const v = e.target.value;
                  setNewVatPct(v);
                  if (!norm(newTaxRateId)) {
                    const mapped = findTaxRateIdForPct(taxRates, v);
                    if (mapped) setNewTaxRateId(String(mapped));
                  }
                }}
                disabled={newItemBusy}
                {...stopGridCaptureProps}
              >
                <option value="0">0</option>
                <option value="5">5</option>
                <option value="20">20</option>
              </select>
            </div>

            <button
              type="button"
              className="min-h-[44px] w-full md:w-auto"
              style={newItemBusy ? buttonDisabled : buttonAction}
              onClick={addItem}
              disabled={newItemBusy}
              {...stopGridCaptureProps}
            >
              {newItemBusy ? "Adding…" : "Add"}
            </button>

            <div className="col-span-full mt-1.5 grid grid-cols-1 gap-2.5 md:grid-cols-2">
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Unit cost (optional)</div>
                <input
                  style={input}
                  value={newUnitCost}
                  onChange={(e) => setNewUnitCost(e.target.value)}
                  placeholder="e.g. 60.00"
                  type="number"
                  disabled={newItemBusy}
                  {...stopGridCaptureProps}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Tax rate (optional override)</div>
                {taxRates.length ? (
                  <select
                    style={select}
                    value={newTaxRateId}
                    onChange={(e) => setNewTaxRateId(e.target.value)}
                    disabled={newItemBusy}
                    {...stopGridCaptureProps}
                  >
                    <option value="">(auto from VAT% / none)</option>
                    {taxRates
                      .slice()
                      .sort((a, b) => Number(a?.id) - Number(b?.id))
                      .map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          #{t.id} — {t?.label || t?.code || "Tax"} ({Math.round((t?.rate ?? 0) * 10000) / 100}%)
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    style={input}
                    value={newTaxRateId}
                    onChange={(e) => setNewTaxRateId(e.target.value)}
                    placeholder="tax_rate_id (numeric or blank)"
                    disabled={newItemBusy}
                    {...stopGridCaptureProps}
                  />
                )}

                {(() => {
                  const explicit = norm(newTaxRateId) ? toIntOrNull(newTaxRateId) : null;
                  const mapped = explicit ? null : findTaxRateIdForPct(taxRates, newVatPct);
                  const pct = Math.round(rateToPct(pctToRate(newVatPct)));
                  if (explicit) return <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Override: tax_rate_id = #{explicit}</div>;
                  if (mapped) return <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Auto: VAT {pct}% → tax_rate_id #{mapped}</div>;
                  return <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Auto: VAT {pct}% → (no matching tax rate found; will send null)</div>;
                })()}
              </div>
            </div>

            <div className="col-span-full grid grid-cols-1 gap-2.5 md:grid-cols-2">
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Effective from (optional)</div>
                <input style={input} value={newEffFrom} onChange={(e) => setNewEffFrom(e.target.value)} type="date" disabled={newItemBusy} {...stopGridCaptureProps} />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Effective to (optional)</div>
                <input style={input} value={newEffTo} onChange={(e) => setNewEffTo(e.target.value)} type="date" disabled={newItemBusy} {...stopGridCaptureProps} />
              </div>
            </div>

            <div className="col-span-full">
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 900 }}>
                <input
                  type="checkbox"
                  checked={newItemActive}
                  onChange={(e) => setNewItemActive(e.target.checked)}
                  disabled={newItemBusy}
                  {...stopGridCaptureProps}
                />
                Active
              </label>
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          {pbItems.length === 0 ? (
            <div style={{ color: "#6b7280", fontWeight: 900 }}>No items in this pricebook.</div>
          ) : (
            pbItems.map((it) => (
              <PricebookItemRow
                key={it.id}
                it={it}
                inputStyle={input}
                selectStyle={select}
                taxRates={taxRates}
                buttonAction={buttonAction}
                buttonDisabled={buttonDisabled}
                patchItem={patchItem}
                deactivateItem={deactivateItem}
              />
            ))
          )}
        </div>
      </div>
    );
  };

  const renderModal = () => {
    if (!modalOpen) return null;

    const canEditCode = mode === "create";

    return createPortal(
      <div
        role="dialog"
        aria-modal="true"
        className="items-start sm:items-center"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          display: "flex",
          justifyContent: "center",
          padding: 8,
          zIndex: 9999,
          overflowY: "auto",
        }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <div
          style={{
            width: "min(980px, 100%)",
            minWidth: 0,
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 18px 40px rgba(0,0,0,0.20)",
            padding: 14,
            maxHeight: "calc(100dvh - 16px)",
            overflow: "auto",
            boxSizing: "border-box",
          }}
          {...stopGridCaptureProps}
        >
          <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div style={{ fontWeight: 900, fontSize: 16, fontFamily: theme?.font_secondary || theme?.font_primary || "inherit" }}>
              {mode === "create" ? "Create Pricebook" : `Edit Pricebook #${editing?.id}`}
            </div>
            <button
              type="button"
              className="min-h-[44px] w-full sm:w-auto"
              onClick={closeModal}
              style={buttonGeneral}
              {...stopGridCaptureProps}
            >
              Close
            </button>
          </div>

          {err ? <div style={{ color: "#b91c1c", fontWeight: 900, marginTop: 10 }}>{err}</div> : null}

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Code</div>
              <input
                style={{ ...input, opacity: canEditCode ? 1 : 0.6 }}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. DEFAULT-2026"
                disabled={!canEditCode}
                {...stopGridCaptureProps}
              />
              {!canEditCode ? <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Code is immutable after creation.</div> : null}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Label</div>
              <input style={input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Human readable name" {...stopGridCaptureProps} />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Flags</div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 900 }}>
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} {...stopGridCaptureProps} />
                Active
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 900 }}>
                <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} {...stopGridCaptureProps} />
                Tenant default
              </label>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Setting default will unset the previous tenant default (DB trigger enforces).</div>
            </div>

            <div style={{ display: "grid", gap: 10 }}>{renderScopePicker()}</div>
          </div>

          <div className="mt-3.5 flex flex-col gap-2.5 sm:flex-row sm:justify-end">
            {mode === "edit" ? (
              <button
                type="button"
                className="min-h-[44px] w-full sm:w-auto"
                style={buttonAction}
                onClick={() => deactivate(editing)}
                {...stopGridCaptureProps}
              >
                Deactivate
              </button>
            ) : null}
            <button
              type="button"
              className="min-h-[44px] w-full sm:w-auto"
              style={buttonAction}
              onClick={save}
              {...stopGridCaptureProps}
            >
              {mode === "create" ? "Create" : "Save changes"}
            </button>
          </div>

          {renderItemsPanel()}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="h-auto min-h-0 w-full overflow-visible md:min-h-[420px]" style={card} {...stopGridCaptureProps}>
      <div className="flex-col items-stretch sm:flex-row sm:items-center sm:justify-between" style={titleStyle}>
        <div>Pricebooks</div>
        <div className="flex w-full flex-col gap-2.5 sm:w-auto sm:flex-row sm:items-center">
          <span className="w-fit" style={badge}>Tenant-scoped</span>
          <button
            type="button"
            className="min-h-[44px] w-full sm:w-auto"
            onClick={openCreate}
            style={buttonAction}
            {...stopGridCaptureProps}
          >
            New pricebook
          </button>
        </div>
      </div>

      {err ? <div style={{ color: "#b91c1c", fontWeight: 900, marginBottom: 10 }}>{err}</div> : null}
      {loading ? <div style={{ fontWeight: 900, marginBottom: 10 }}>Loading…</div> : null}

      <div className="mb-3 grid grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
        <input style={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search (code / label / scope / id)" {...stopGridCaptureProps} />
        <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 900, color: "#111827" }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} {...stopGridCaptureProps} />
          Include inactive
        </label>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {filtered.length === 0 ? (
          <div style={{ color: "#6b7280", fontWeight: 900 }}>No pricebooks found.</div>
        ) : (
          filtered.map((pb) => (
            <div
              key={pb.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 12,
                display: "grid",
                gap: 8,
                minWidth: 0,
                overflowWrap: "anywhere",
              }}
              {...stopGridCaptureProps}
            >
              <div className="grid min-w-0 grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                <div style={{ fontWeight: 900 }}>
                  {pb.label} <span style={{ fontWeight: 900, color: "#6b7280" }}>({pb.code}) #{pb.id}</span>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    className="min-h-[44px] w-full sm:w-auto"
                    onClick={() => openEdit(pb)}
                    style={buttonGeneral}
                    {...stopGridCaptureProps}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="min-h-[44px] w-full sm:w-auto"
                    onClick={() => deactivate(pb)}
                    style={pb?.active ? buttonAction : buttonDisabled}
                    disabled={!pb?.active}
                    {...stopGridCaptureProps}
                  >
                    Deactivate
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
                <Info label="Scope" value={scopeLabel(pb)} />
                <Info label="Default" value={pb.is_default ? "Yes" : "No"} />
                <Info label="Active" value={pb.active ? "Yes" : "No"} />
                <Info label="Updated" value={pb.updated_at ? String(pb.updated_at).replace("T", " ").slice(0, 19) : "—"} />
              </div>
            </div>
          ))
        )}
      </div>

      {renderModal()}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>{label}</div>
      <div style={{ fontWeight: 900 }}>{value ?? "—"}</div>
    </div>
  );
}

function PricebookItemRow({ it, selectStyle, inputStyle, taxRates, buttonAction, buttonDisabled, patchItem, deactivateItem }) {
  const stopGridEvent = (e) => {
    try {
      e.stopPropagation?.();
    } catch {}
  };

  const stopGridCaptureProps = {
    onPointerDownCapture: stopGridEvent,
    onMouseDownCapture: stopGridEvent,
    onTouchStartCapture: stopGridEvent,
  };

  const getServiceText = (x) => {
    const candidates = [x?.description, x?.service_name, x?.service_text, x?.name, x?.title, x?.service?.name, x?.service?.label];
    for (const c of candidates) {
      const s = String(c ?? "").trim();
      if (s) return s;
    }
    return "";
  };

  const [descDraft, setDescDraft] = useState(getServiceText(it));
  const [unitPriceDraft, setUnitPriceDraft] = useState(it.unit_price ?? "");
  const [unitCostDraft, setUnitCostDraft] = useState(it.unit_cost ?? "");
  const [taxRateDraft, setTaxRateDraft] = useState(it.tax_rate_id ?? "");
  const [activeDraft, setActiveDraft] = useState(it.active ? "1" : "0");
  const [effFromDraft, setEffFromDraft] = useState(it.effective_from ?? "");
  const [effToDraft, setEffToDraft] = useState(it.effective_to ?? "");

  useEffect(() => {
    setDescDraft(getServiceText(it));
    setUnitPriceDraft(it.unit_price ?? "");
    setUnitCostDraft(it.unit_cost ?? "");
    setTaxRateDraft(it.tax_rate_id ?? "");
    setActiveDraft(it.active ? "1" : "0");
    setEffFromDraft(it.effective_from ?? "");
    setEffToDraft(it.effective_to ?? "");
  }, [it.id, it.description, it.service_name, it.service_text, it.unit_price, it.unit_cost, it.tax_rate_id, it.active, it.effective_from, it.effective_to]);

  const commitDesc = async () => {
    const next = String(descDraft ?? "").trim();
    const cur = getServiceText(it);
    if (!next) return;
    if (cur === next) return;

    // Patch multiple aliases to match backend reality.
    await patchItem(it.id, { description: next, service_name: next, service_text: next });
  };

  const commitUnitPrice = async () => {
    const v = unitPriceDraft === "" ? null : Number(unitPriceDraft);
    if (v == null || Number.isNaN(v)) return;
    if (Number(it.unit_price) === Number(v)) return;
    await patchItem(it.id, { unit_price: Number(v) });
  };

  const commitUnitCost = async () => {
    const v = unitCostDraft === "" ? null : Number(unitCostDraft);
    if (v !== null && Number.isNaN(v)) return;
    const current = it.unit_cost == null ? null : Number(it.unit_cost);
    const next = v == null ? null : Number(v);
    if (current === next) return;
    await patchItem(it.id, { unit_cost: next });
  };

  const commitTaxRate = async (raw) => {
    const next = raw ? Number(raw) : null;
    const cur = it.tax_rate_id == null ? null : Number(it.tax_rate_id);
    if (cur === next) return;
    await patchItem(it.id, { tax_rate_id: next });
  };

  const commitActive = async (raw) => {
    const next = raw === "1";
    if (Boolean(it.active) === Boolean(next)) return;
    await patchItem(it.id, { active: next });
  };

  const commitEffFrom = async (raw) => {
    const next = raw || null;
    const cur = it.effective_from || null;
    if (cur === next) return;
    await patchItem(it.id, { effective_from: next });
  };

  const commitEffTo = async (raw) => {
    const next = raw || null;
    const cur = it.effective_to || null;
    if (cur === next) return;
    await patchItem(it.id, { effective_to: next });
  };

  const taxLabel = (t) => {
    const pct = Math.round((Number(t?.rate ?? 0) * 10000) / 100);
    return `#${t?.id} — ${t?.label || t?.code || "Tax"} (${pct}%)`;
  };

  const headerText = (() => {
    const s = getServiceText(it);
    if (s) return s;
    return `Item #${it.id}`;
  })();

  return (
    <div className="min-w-0" style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, display: "grid", gap: 10, overflowWrap: "anywhere" }} {...stopGridCaptureProps}>
      <div className="grid min-w-0 grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
        <div style={{ fontWeight: 900 }}>
          {headerText} <span style={{ color: "#6b7280" }}>(Item #{it.id})</span>
        </div>

        <button
          type="button"
          className="min-h-[44px] w-full sm:w-auto"
          style={it.active ? buttonAction : buttonDisabled}
          disabled={!it.active}
          onClick={() => deactivateItem(it.id)}
          {...stopGridCaptureProps}
          title="Soft delete (active=false)"
        >
          Deactivate
        </button>
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Service</div>
        <input
          style={inputStyle}
          value={descDraft}
          onChange={(e) => setDescDraft(e.target.value)}
          onBlur={commitDesc}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitDesc();
            }
          }}
          placeholder="Service (free text)"
          {...stopGridCaptureProps}
        />
      </div>

      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Unit price</div>
          <input
            style={inputStyle}
            value={unitPriceDraft}
            onChange={(e) => setUnitPriceDraft(e.target.value)}
            onBlur={commitUnitPrice}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitUnitPrice();
              }
            }}
            type="number"
            {...stopGridCaptureProps}
          />
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Unit cost</div>
          <input
            style={inputStyle}
            value={unitCostDraft}
            onChange={(e) => setUnitCostDraft(e.target.value)}
            onBlur={commitUnitCost}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitUnitCost();
              }
            }}
            type="number"
            {...stopGridCaptureProps}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Tax rate</div>
          {Array.isArray(taxRates) && taxRates.length ? (
            <select
              style={selectStyle}
              value={taxRateDraft == null ? "" : String(taxRateDraft)}
              onChange={(e) => {
                const v = e.target.value;
                setTaxRateDraft(v);
                commitTaxRate(v);
              }}
              {...stopGridCaptureProps}
            >
              <option value="">(none)</option>
              {taxRates
                .slice()
                .sort((a, b) => Number(a?.id) - Number(b?.id))
                .map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {taxLabel(t)}
                  </option>
                ))}
            </select>
          ) : (
            <input
              style={inputStyle}
              value={taxRateDraft ?? ""}
              onChange={(e) => setTaxRateDraft(e.target.value)}
              onBlur={() => commitTaxRate(String(taxRateDraft || ""))}
              placeholder="tax_rate_id (numeric or blank)"
              {...stopGridCaptureProps}
            />
          )}
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Active</div>
          <select
            style={selectStyle}
            value={activeDraft}
            onChange={(e) => {
              const v = e.target.value;
              setActiveDraft(v);
              commitActive(v);
            }}
            {...stopGridCaptureProps}
          >
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Effective from</div>
          <input
            style={inputStyle}
            value={effFromDraft ?? ""}
            onChange={(e) => setEffFromDraft(e.target.value)}
            onBlur={() => commitEffFrom(String(effFromDraft || ""))}
            type="date"
            {...stopGridCaptureProps}
          />
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Effective to</div>
          <input
            style={inputStyle}
            value={effToDraft ?? ""}
            onChange={(e) => setEffToDraft(e.target.value)}
            onBlur={() => commitEffTo(String(effToDraft || ""))}
            type="date"
            {...stopGridCaptureProps}
          />
        </div>
      </div>

      {it.updated_at ? <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Updated: {String(it.updated_at).replace("T", " ").slice(0, 19)}</div> : null}
    </div>
  );
}
